import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Center, Group, Loader, NumberInput, Paper, SimpleGrid, Stack, Switch, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, inboxApi, type InboxSettings, type InboxStatus } from '../api';
import { INBOX_OUTCOMES, formatDateTime } from '../format';
import { LibraryTable, type LibraryColumn } from '../ui/LibraryTable';

const errorOf = (e: unknown): string => (e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.');

type InboxRun = InboxStatus['recent'][number];

const RUN_COLUMNS: LibraryColumn<InboxRun>[] = [
  { key: 'at', title: 'Zeit', defaultWidth: 150, render: (r) => <Text size="xs">{formatDateTime(r.at)}</Text> },
  { key: 'folder', title: 'Ordner', defaultWidth: 100, render: (r) => <Text size="xs" ff="monospace">{r.folder}/</Text> },
  { key: 'fileName', title: 'Datei', defaultWidth: 240, minWidth: 80, render: (r) => <Text size="sm">{r.fileName}</Text> },
  { key: 'outcome', title: 'Ergebnis', defaultWidth: 130, render: (r) => <Badge size="sm" variant="light" color={INBOX_OUTCOMES[r.outcome].color}>{INBOX_OUTCOMES[r.outcome].label}</Badge> },
  { key: 'summary', title: 'Zusammenfassung', defaultWidth: 320, minWidth: 80, render: (r) => <Text size="xs" lineClamp={2}>{r.summary}</Text> },
  { key: 'mailed', title: 'Mail', defaultWidth: 80, render: (r) => <Text size="xs" c="dimmed">{r.mailed ? 'gesendet' : '—'}</Text> },
];

/**
 * The inbox folder (ADR 0009) for the administration: switch it on, where
 * receipts are mailed, how long they stay, and what PST found - files read,
 * files waiting, files outside a known folder.
 */
export function InboxPanel(): JSX.Element {
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ['inbox'], queryFn: inboxApi.status, refetchInterval: 30_000 });
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['inbox'] });
  const save = useMutation({ mutationFn: (body: Partial<InboxSettings>) => inboxApi.save(body), onSettled: refresh });
  const scan = useMutation({ mutationFn: () => inboxApi.scan(), onSettled: refresh });
  const [mail, setMail] = useState('');
  useEffect(() => { setMail(status.data?.settings.notifyEmail ?? ''); }, [status.data?.settings.notifyEmail]);

  if (status.isPending) return <Center h={120}><Loader size="sm" /></Center>;
  if (status.isError) return <Alert color="red">{errorOf(status.error)}</Alert>;
  const s = status.data;

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Dateien in einem Unterordner des Eingangs liest PST selbst ein – jede Datei einmal, erkannt am Inhalt; im Eingang wird nichts verschoben oder gelöscht. Zu jeder Datei legt PST eine Quittung in den Ausgang und schickt sie auf Wunsch per Mail. Dateien außerhalb der Unterordner bleiben unberührt.
      </Text>
      {(save.error ?? scan.error) && <Alert color="red" py={6}>{errorOf(save.error ?? scan.error)}</Alert>}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
        <Paper withBorder radius="md" p="sm">
          <Stack gap="xs">
            <Switch label="Eingang überwachen (jede Minute)" checked={s.settings.enabled} disabled={save.isPending} onChange={(e) => save.mutate({ enabled: e.currentTarget.checked })} />
            <Group gap="xs" align="flex-end" wrap="nowrap">
              <TextInput size="xs" label="Quittungen per Mail an" placeholder="leer: keine Mail" value={mail} onChange={(e) => setMail(e.currentTarget.value)} style={{ flex: 1 }}
                description={s.mailConfigured ? undefined : 'Für PST ist kein Mailversand eingerichtet (SMTP in .env).'} />
              <Button size="xs" variant="light" disabled={(mail.trim() || null) === s.settings.notifyEmail} loading={save.isPending} onClick={() => save.mutate({ notifyEmail: mail.trim() || null })}>Speichern</Button>
            </Group>
            <NumberInput size="xs" label="Quittungen aufbewahren (Tage)" min={1} max={3650} value={s.settings.receiptDays} w={220}
              onBlur={(e) => { const v = Number(e.currentTarget.value); if (v >= 1 && v !== s.settings.receiptDays) save.mutate({ receiptDays: v }); }} />
          </Stack>
        </Paper>
        <Paper withBorder radius="md" p="sm">
          <Stack gap={4}>
            <Text size="sm">Eingang <Text span ff="monospace" size="xs">{s.inbox.path}</Text>: {s.inbox.readable ? <Text span c="green.7">lesbar</Text> : <Text span c="red">nicht erreichbar</Text>}</Text>
            <Text size="sm">Ausgang <Text span ff="monospace" size="xs">{s.outbox.path}</Text>: {s.outbox.writable ? <Text span c="green.7">beschreibbar</Text> : <Text span c="red">nicht beschreibbar</Text>}</Text>
            {s.folders.map((f) => (
              <Text key={f.folder} size="xs"><Text span ff="monospace">{f.folder}/</Text> → {f.label} ({f.extensions.join(', ')})</Text>
            ))}
            <Text size="xs" c="dimmed">Zuletzt geprüft: {s.lastScan ? formatDateTime(s.lastScan) : '—'}</Text>
            <Group gap="xs"><Button size="xs" variant="default" loading={scan.isPending} onClick={() => scan.mutate()}>Jetzt prüfen</Button>
              {scan.data && <Text size="xs">{scan.data.handled} Datei{scan.data.handled === 1 ? '' : 'en'} verarbeitet</Text>}</Group>
          </Stack>
        </Paper>
      </SimpleGrid>
      {(!s.inbox.readable || !s.outbox.writable) && <Alert color="orange" py={6}>Eingang oder Ausgang sind für PST nicht nutzbar. Auf dem Server einmal <Text span ff="monospace">ops/eingang-einrichten.sh</Text> ausführen und PST neu starten.</Alert>}
      {s.waiting.length > 0 && <Text size="xs" c="dimmed">Wird noch geschrieben, beim nächsten Durchlauf: {s.waiting.join(', ')}</Text>}
      {s.unassigned.length > 0 && (
        <Alert color="gray" py={6} title="Nicht zugeordnet – wird nicht eingelesen">
          {s.unassigned.map((u) => <Text key={u.path} size="xs"><Text span ff="monospace">{u.path}</Text>: {u.reason}</Text>)}
        </Alert>
      )}
      {s.recent.length === 0 ? <Text size="sm" c="dimmed">Noch keine Datei aus dem Eingang verarbeitet.</Text> : (
        // What PST did with a file - looked at, never changed (AP-128).
        <LibraryTable<InboxRun>
          tableId="eingang"
          label="Verarbeitete Dateien aus dem Eingang"
          columns={RUN_COLUMNS}
          rows={s.recent}
          rowKey={(r) => r.id}
          rowLabel={(r) => r.fileName}
          canManage={false}
          fields={(r) => [
            { key: 'at', label: 'Zeit', value: formatDateTime(r.at) },
            { key: 'folder', label: 'Ordner', value: `${r.folder}/`, mono: true },
            { key: 'fileName', label: 'Datei', value: r.fileName },
            { key: 'outcome', label: 'Ergebnis', value: INBOX_OUTCOMES[r.outcome].label },
            { key: 'mailed', label: 'Mail', value: r.mailed ? 'gesendet' : 'nicht gesendet' },
            { key: 'receipt', label: 'Quittung', value: r.receipt ?? 'nicht geschrieben', mono: r.receipt !== null },
            { key: 'summary', label: 'Zusammenfassung', value: r.summary, long: true },
            { key: 'details', label: 'Einzelheiten', value: r.details, long: true, mono: true },
          ]}
        />
      )}
    </Stack>
  );
}
